import React, { useState } from "react";
import { Box, Typography, Button, IconButton, Grid, Paper, FormHelperText, Popover, TextField, InputAdornment } from "@mui/material";
import { FieldArray, getIn } from "formik";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { DateRangePicker } from "react-date-range";
import { format, parseISO } from "date-fns";
import enUS from "date-fns/locale/en-US";

import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file

const DateRangeSelector = ({ index, dateRange, setFieldValue, startError, endError, startTouched, endTouched }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleSelect = (ranges) => {
    const { selection } = ranges;
    setFieldValue(`season_dates[${index}].start`, format(selection.startDate, 'MM-dd'));
    setFieldValue(`season_dates[${index}].end`, format(selection.endDate, 'MM-dd'));
  };

  const currentYear = new Date().getFullYear();
  const startDate = dateRange.start ? parseISO(`${currentYear}-${dateRange.start}`) : new Date();
  const endDate = dateRange.end ? parseISO(`${currentYear}-${dateRange.end}`) : new Date();

  const selectionRange = {
    startDate: startDate,
    endDate: endDate,
    key: "selection",
  };

  const displayValue = dateRange.start && dateRange.end 
    ? `${format(startDate, 'MMM dd')} - ${format(endDate, 'MMM dd')}`
    : "Select a date range";

  const hasError = (startTouched && Boolean(startError)) || (endTouched && Boolean(endError));
  const helperText = (startTouched && startError) || (endTouched && endError) || "";

  return (
    <>
      <TextField
        fullWidth
        label="Season Date Range"
        value={displayValue}
        onClick={handleClick}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <CalendarMonthIcon color="action" />
            </InputAdornment>
          ),
          sx: { cursor: 'pointer' }
        }}
        error={hasError}
        helperText={helperText}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <DateRangePicker
          ranges={[selectionRange]}
          onChange={handleSelect}
          rangeColors={["#1976d2"]}
          showMonthAndYearPickers={true}
        />
      </Popover>
    </>
  );
};

const SectionSeasonDates = ({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => {
  return (
    <Box mt={3}>
      <Typography variant="subtitle1" fontWeight={600} mb={1}>
        Season Dates
      </Typography>

      <FieldArray name="season_dates">
        {({ push, remove }) => {
          const seasonDatesErrors = typeof errors.season_dates === 'string' ? errors.season_dates : null;

          return (
            <Paper variant="outlined" sx={{ p: 2, borderColor: "#e0e0e0" }}>
              {values.season_dates && values.season_dates.length > 0 ? (
                values.season_dates.map((dateRange, index) => {
                  const startError = getIn(errors, `season_dates[${index}].start`);
                  const startTouched = getIn(touched, `season_dates[${index}].start`);
                  const endError = getIn(errors, `season_dates[${index}].end`);
                  const endTouched = getIn(touched, `season_dates[${index}].end`);

                  return (
                    <Box
                      key={index}
                      mb={2}
                      p={2}
                      border={1}
                      borderColor="#f0f0f0"
                      borderRadius={1}
                      position="relative"
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={10}>
                          <DateRangeSelector 
                            index={index}
                            dateRange={dateRange}
                            setFieldValue={setFieldValue}
                            startError={startError}
                            endError={endError}
                            startTouched={startTouched}
                            endTouched={endTouched}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2} textAlign="right">
                          <IconButton
                            color="error"
                            onClick={() => remove(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary" mb={2}>
                  No season dates added.
                </Typography>
              )}

              {seasonDatesErrors && (
                <FormHelperText error>{seasonDatesErrors}</FormHelperText>
              )}

              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => push({ start: "", end: "" })}
              >
                Add Season Date Range
              </Button>
            </Paper>
          );
        }}
      </FieldArray>
    </Box>
  );
};

export default SectionSeasonDates;
